import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SOSButton } from "@/components/SOSButton";
import { SobrietyCounter } from "@/components/tracker/SobrietyCounter";
import { DailyPledgeCard } from "@/components/tracker/DailyPledgeCard";
import { DailyQuoteCard } from "@/components/tracker/DailyQuoteCard";
import { useSobrietyTracker } from "@/hooks/useSobrietyTracker";
import {
  ShieldAlert, BookOpen, Heart, FileText, User, Eye, LogOut, Settings,
  Lock, CheckCircle, Timer, ArrowRight,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface RecentResource {
  id: string;
  title: string;
  slug: string;
}

export default function Home() {
  const { user, isAdmin, isModerator, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [recentResources, setRecentResources] = useState<RecentResource[]>([]);
  const { trackers, resetTracker, deleteTracker } = useSobrietyTracker();

  const userInitials = user?.email?.split("@")[0].slice(0, 2).toUpperCase() || "U";

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  useEffect(() => {
    async function fetchRecent() {
      const { data } = await supabase
        .from("rdr_resources")
        .select("id, title, slug")
        .order("created_at", { ascending: false })
        .limit(3);
      if (data) setRecentResources(data);
    }
    fetchRecent();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) return null;

  const mainTracker = trackers[0] ?? null;

  return (
    <div className="min-h-dvh bg-background">
      <SOSButton />

      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 w-10 rounded-full p-0">
                  <Avatar>
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <div className="px-2 py-1.5 text-sm font-medium">{user?.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Mon Profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8">
        {/* Hero */}
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold">
            Bonjour {user.email?.split("@")[0]} 👋
          </h1>
          <p className="text-muted-foreground text-lg">
            Comment peut-on t'aider aujourd'hui ?
          </p>
        </div>

        {/* Daily quote (compact) */}
        <DailyQuoteCard compact />

        {/* Tracker + Pledge row */}
        {(mainTracker || true) && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Mini tracker or CTA */}
            {mainTracker ? (
              <div
                className="cursor-pointer"
                onClick={() => navigate("/tracker")}
              >
                <SobrietyCounter
                  tracker={mainTracker}
                  onReset={resetTracker}
                  onDelete={deleteTracker}
                  compact
                />
              </div>
            ) : (
              <Card
                className="glass-card border-primary/20 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate("/tracker")}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                    <Timer className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">Compteur de sobriété</p>
                    <p className="text-xs text-muted-foreground">
                      Commence à tracker ta progression
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </CardContent>
              </Card>
            )}

            {/* Pledge compact */}
            <div
              className="cursor-pointer"
              onClick={() => navigate("/tracker")}
            >
              <DailyPledgeCard compact />
            </div>
          </div>
        )}

        {trackers.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-primary"
            onClick={() => navigate("/tracker")}
          >
            Voir mes {trackers.length} compteurs →
          </Button>
        )}

        {/* 3 Zone Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Urgence */}
          <Card className="border border-destructive/30 hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-destructive/15 flex items-center justify-center mb-2">
                <ShieldAlert className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle>Urgence</CardTitle>
              <CardDescription>En cas de danger immédiat</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="destructive" className="w-full" onClick={() => navigate("/panic")}>
                Numéros d'urgence
              </Button>
              <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/directory")}>
                Annuaire CSAPA/CJC
              </Button>
            </CardContent>
          </Card>

          {/* S'informer */}
          <Card className="border border-primary/30 hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-2">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <CardTitle>S'informer</CardTitle>
              <CardDescription>Ressources RDR vérifiées</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentResources.length > 0 && (
                <ul className="space-y-1.5">
                  {recentResources.map((r) => (
                    <li key={r.id}>
                      <button
                        onClick={() => navigate(`/resources/${r.slug}`)}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left truncate w-full flex items-center gap-2"
                      >
                        <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{r.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <Button className="w-full" onClick={() => navigate("/resources")}>
                Ressources RDR
              </Button>
              <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/withdrawal")}>
                Timeline de sevrage
              </Button>
            </CardContent>
          </Card>

          {/* Être accompagné — verrouillé */}
          <Card className="border border-accent/30 opacity-75 relative">
            <Badge className="absolute top-3 right-3 bg-accent/15 text-accent border-accent/30">
              <Lock className="w-3 h-3 mr-1" />
              Bientôt
            </Badge>
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center mb-2">
                <Heart className="w-6 h-6 text-accent" />
              </div>
              <CardTitle>Être accompagné</CardTitle>
              <CardDescription>Accompagnement professionnel</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-accent/50" />
                  Trouver un praticien
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-accent/50" />
                  Téléconsultation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-accent/50" />
                  Suivi personnalisé
                </li>
              </ul>
              <p className="text-xs italic text-muted-foreground/70 text-center">
                Recherche de financement en cours
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Admin/Moderation Access */}
        {(isModerator || isAdmin) && (
          <div className="grid md:grid-cols-2 gap-4">
            {isModerator && (
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/moderation")}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Eye className="w-6 h-6 text-accent" />
                    <div>
                      <CardTitle className="text-base">Modération</CardTitle>
                      <CardDescription className="text-sm">Vérification et qualité</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            )}
            {isAdmin && (
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate("/admin/practitioners")}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <User className="w-6 h-6 text-destructive" />
                    <div>
                      <CardTitle className="text-base">Administration</CardTitle>
                      <CardDescription className="text-sm">Gérer la plateforme</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
